import chroma from 'chroma-js'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { VIDEO_FILE } from '@disco/constants/FileTypes'
import { PLAYING } from '@disco/constants/MediaStatus'
import {
  getTrackWaveform,
  pauseTrack,
  playTrack,
  seekTrackPosition,
} from '@disco/state-management/actions/MediaActions'
import type { TrackStatus } from '@disco/state-management/reducers/media'
import {
  getTrackPlaybackStatus,
  getTrackPlaying,
  getTrackWaveformStatus,
} from '@disco/state-management/selectors/media'
import { formatColor, parseColor } from '@disco/utils/color-utils'
import { getFileTypeByExt } from '@disco/utils/file-utils'

import type { Track } from 'types/track'
import type { EventSource } from 'types/ui'

import { useDispatch, useSelector } from 'hooks/use-redux'

import Canvas from './canvas'

const DEFAULT_STATUS = {} as TrackStatus

const getSolidColor = (
  index,
  fadePos,
  activeColorStr,
  fadeColorStr,
  alphaPos,
  theme = 'light',
) => {
  const inactiveColor = parseColor(theme === 'dark' ? '#444444' : '#BBBBBB')
  const fadeColor = parseColor(fadeColorStr)
  const activeColor = parseColor(activeColorStr)
  let resultColor

  if (index < fadePos) {
    resultColor = activeColor
  } else {
    resultColor = inactiveColor
  }

  if (alphaPos !== -1) {
    if (alphaPos > fadePos) {
      // Logic for alphaPos to the right of fadePos
      if (fadePos < index && index < alphaPos) {
        resultColor = fadeColor
      }

      if (alphaPos < index && index < fadePos) {
        resultColor = inactiveColor
      }
    } else {
      // Logic for alphaPos to the left of fadePos
      if (alphaPos < index && index < fadePos) {
        // Fade only the overlap between alphaPos and fadePos
        resultColor = fadeColor
      }
    }
  }

  return formatColor(resultColor)
}

const updateColorFromStyleValue = ({
  node,
  theme,
  setColor,
  setColorFade,
}: {
  node: HTMLElement
  theme: 'light' | 'dark'
  setColor: (color: string) => void
  setColorFade: (color: string) => void
}) => {
  const accentColor = theme === 'dark' ? '--disco-yellow' : '--accent-9'

  try {
    const variableValue = window
      .getComputedStyle(node)
      .getPropertyValue(accentColor)
      .trim()
    const variableValue2 = window
      .getComputedStyle(node)
      .getPropertyValue('--accent-7')
      .trim()
    if (variableValue) {
      setColor(chroma(variableValue).hex('rgb'))
    }
    if (variableValue2) {
      setColorFade(chroma(variableValue2).hex('rgb'))
    }
  } catch (e) {
    console.error(e)
  }
}

const isPlaybackWithinRange = (
  value: number,
  target: number,
  duration: number,
  allowanceRatio: number = 0.01, // 1% of total track duration
) => Math.abs(value - target) <= duration * allowanceRatio

type Props = {
  libraryId?: number
  playContext?: EventSource
  className?: string
  track: Record<string, any>
  hoverSeekPosition?: number
  hover?: number
  barWidth: number
  barGap: number
  noAnimation?: boolean
  noSeekClick?: boolean
  onSeek?: (...args: Array<any>) => any
  isHoverSeekEnabled?: boolean
  // unified mobile player related props
  scrollable?: boolean
  // scrollable behavior for mobile browsers
  theme?: 'light' | 'dark'
  height?: number
  waveformLocation?: string
}

const Waveform = (props: Props) => {
  const DPR = window.devicePixelRatio || 1

  const {
    barWidth = 2,
    barGap = 1,
    height = 40,
    playContext = 'in-app',
    hover: hoverFromProps,
  } = props

  const dispatch = useDispatch()

  const containerRef = useRef<HTMLElement | null>(null)
  const animationStart = useRef(0)
  const lastWaveform = useRef<any>(null)
  const widthRef = useRef(0)

  const [scrubbing, setScrubbing] = useState(false)
  const [hover, setHover] = useState(-1)
  const [width, setWidth] = useState(0)
  const [hoverSeekPosition, setHoverSeekPosition] = useState(0)
  const [color, setColor] = useState('#222222')
  const [colorFade, setColorFade] = useState('#888888')
  const [prevWaveform, setPrevWaveform] = useState<any>(null)

  const currentlyPlayingTrack = useSelector(getTrackPlaying)

  const status = useSelector(state =>
    props.track
      ? getTrackPlaybackStatus(state, {
          trackId: props.track.id,
        }) || DEFAULT_STATUS
      : DEFAULT_STATUS,
  )

  const playbackPosition = status.playbackPosition
  const playbackStatus = status.playbackStatus

  const waveformStatus = useSelector(state =>
    props.track
      ? getTrackWaveformStatus(state, { trackId: props.track.id }) || {}
      : {},
  )

  const waveformLoadStatus = waveformStatus?.waveformLoadStatus

  const waveformData = useMemo(() => {
    if (props.track?.waveform) {
      try {
        return JSON.parse(props.track.waveform)
      } catch (e) {
        console.error('Failed to parse waveform', e)
      }
    }
    return waveformStatus?.waveformData
  }, [props.track?.waveform, waveformStatus?.waveformData])

  useEffect(() => {
    if (waveformData !== lastWaveform.current) {
      setPrevWaveform(lastWaveform.current)
    }

    lastWaveform.current = waveformData
  }, [waveformData])

  const measureAndSaveSize = useCallback(() => {
    if (containerRef.current) {
      const nextWidth = containerRef.current.getBoundingClientRect().width

      if (nextWidth !== widthRef.current) {
        widthRef.current = nextWidth
        setWidth(nextWidth)
      }
    }
  }, [])

  useEffect(() => {
    measureAndSaveSize()
    window.addEventListener('resize', measureAndSaveSize)
    return () => {
      window.removeEventListener('resize', measureAndSaveSize)
    }
  }, [measureAndSaveSize])

  const trackId = props.track?.id
  const trackWaveform = props.track?.waveform

  useEffect(() => {
    if (trackId && !trackWaveform && waveformLoadStatus === undefined) {
      dispatch(getTrackWaveform(trackId))
    }
  }, [trackId, trackWaveform, waveformLoadStatus, dispatch])

  const setContainerRef = useCallback(
    node => {
      if (node !== null) {
        containerRef.current = node
        const rect = node.getBoundingClientRect()
        setWidth(rect.width)
        updateColorFromStyleValue({
          node,
          theme: props.theme ?? 'light',
          setColor,
          setColorFade,
        })
      }
    },
    [props.theme],
  )

  const handleMouseDown = (evt: MouseEvent | TouchEvent) => {
    setScrubbing(true)
    onSeekClick(evt)

    //Added additional check due to zIndex differences.
    //The video modal has an index greater than the waveform index, so the onMouseUp event doesn't fire.
    if (getFileTypeByExt(props.track.ext) === VIDEO_FILE) {
      handleMouseUp()
    }
  }
  const handleMouseUp = () => {
    setScrubbing(false)
  }

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [])
  const handleMouseMove = (evt: MouseEvent | TouchEvent) => {
    const { isHoverSeekEnabled } = props
    const pos = getPos(evt)

    if (scrubbing) {
      onSeekClick(evt)
    } else {
      onSeekHover(evt)
    }
    if (isHoverSeekEnabled) {
      setHoverSeekPosition(pos)
    }
  }

  const handleMouseLeave = () => {
    setHover(-1)
    setHoverSeekPosition(0)
  }

  const getPos = (evt: TouchEvent | MouseEvent) => {
    let clientX
    if ('touches' in evt) {
      clientX = evt.touches[0]?.clientX ?? 0
    } else {
      clientX = evt.clientX
    }
    const el = containerRef.current
    if (!el || !props.track) return 0

    const rect = el.getBoundingClientRect()
    const sx = clientX - rect.left
    const pos = (sx / rect.width) * props.track.duration
    return pos
  }
  const onSeekHover = (e: MouseEvent | TouchEvent) => {
    const pos = getPos(e)
    setHover(pos)
  }

  const onSeekClick = (e: MouseEvent | TouchEvent) => {
    const { isHoverSeekEnabled, track, noSeekClick } = props

    if (noSeekClick) return

    if (!('touches' in e)) {
      e.preventDefault()
    }

    const pos = getPos(e)
    const isWithinRange = isPlaybackWithinRange(
      playbackPosition,
      pos,
      track.duration,
    )

    const shouldSkipSeek =
      playbackStatus === PLAYING && isHoverSeekEnabled && isWithinRange

    if (!scrubbing && shouldSkipSeek) {
      dispatch(pauseTrack(track.id))
      return
    }

    dispatch(seekTrackPosition(track.id, pos, props.waveformLocation))

    if (
      currentlyPlayingTrack == null ||
      track.id !== currentlyPlayingTrack.id
    ) {
      const playOptions = {}
      if (props.libraryId) {
        playOptions['clientLibraryId'] = props.libraryId
      }
      dispatch(playTrack(track.id, playContext, playOptions))
    }

    if (props.onSeek) props.onSeek(pos)
  }
  const drawCanvas = ({ ctx, time }: any) => {
    if (!props.track) return
    const { isHoverSeekEnabled } = props
    const duration = props.track.duration || 1
    let { width, height } = ctx.canvas

    if (prevWaveform && !animationStart.current) {
      animationStart.current = time
    } else if (prevWaveform && time - animationStart.current > 800) {
      animationStart.current = 0
      setPrevWaveform(null)
    }

    ctx.save()
    width = width / DPR
    height = height / DPR
    ctx.scale(DPR, DPR)
    ctx.clearRect(0, 0, width, height)
    let sumAccum = 0
    let sumCount = 0
    let barIndex = 0
    const totalSamples = 1800
    const maxSample = 140
    const calcBarWidth = barWidth * 1.5
    const barSpace = barWidth + barGap
    const totalBars = width / barSpace
    const playbackPos = totalSamples * (playbackPosition / duration)
    const hoverOnlyPlaybackPos =
      isHoverSeekEnabled &&
      hoverSeekPosition &&
      totalSamples * (hoverSeekPosition / duration)
    const hoverPos = totalSamples * ((hoverFromProps || hover) / duration)
    let alphaPos, fadePos

    if (hoverFromProps || hover !== -1) {
      fadePos = playbackPos
      alphaPos = hoverPos
    } else {
      fadePos = playbackPos
      alphaPos = -1
    }

    if (isHoverSeekEnabled && hoverOnlyPlaybackPos && !playbackPosition) {
      fadePos = hoverOnlyPlaybackPos
      alphaPos = 0
    }

    const coef =
      animationStart.current > 0 ? (time - animationStart.current) / 800 : 1

    if (waveformData && waveformData.samples) {
      waveformData.samples.forEach((barHeight, index) => {
        if (prevWaveform && !props.noAnimation) {
          // eslint-disable-next-line no-param-reassign
          barHeight =
            barHeight * coef + prevWaveform.samples[index] * (1 - coef)
        }

        sumAccum += barHeight
        sumCount += 1

        if ((index * totalBars) / totalSamples > barIndex) {
          // eslint-disable-next-line no-param-reassign
          ctx.fillStyle = getSolidColor(
            index,
            fadePos,
            color,
            colorFade,
            alphaPos,
            props.theme,
          )
          const y = ((sumAccum / sumCount) * height) / maxSample
          ctx.fillRect(
            barIndex * barSpace,
            height / 2 - Math.max(Math.round(y / 2), 1),
            calcBarWidth,
            Math.max(Math.round(y / 2), 1) * 2,
          )
          barIndex += 1
          sumAccum = 0
          sumCount = 0
        }
      })
    }

    ctx.restore()
  }

  const calcHeight = props.scrollable ? 54 : height
  return (
    <div ref={setContainerRef} className={props.className}>
      <Canvas
        draw={drawCanvas}
        width={width * DPR}
        height={calcHeight * DPR}
        style={{
          width,
          height,
        }}
        realtime={!!prevWaveform}
        onMouseDown={props.scrollable ? undefined : handleMouseDown}
        onMouseUp={props.scrollable ? undefined : handleMouseUp}
        onMouseMove={props.scrollable ? undefined : handleMouseMove}
        onMouseLeave={props.scrollable ? undefined : handleMouseLeave}
        onTouchStart={props.scrollable ? undefined : handleMouseDown}
        onTouchMove={props.scrollable ? undefined : handleMouseMove}
        onTouchEnd={props.scrollable ? undefined : handleMouseLeave}
      />
    </div>
  )
}

export default Waveform
